import { ApiRequestError } from '@/lib/api';

import {
  browserSubscriptionToRequest,
  fetchPushVapidPublicKey,
  postPushSubscribe,
  postPushUnsubscribeCurrent,
} from './push-api';
import { isPushSupported, readNotificationPermission, urlBase64ToUint8Array } from './push-support';

let registerGate: Promise<void> | null = null;

export type SubscribePushResult = {
  endpoint: string;
};

async function resolveVapidConfig(
  vapidPublicKey?: string,
): Promise<{ enabled: boolean; publicKey: string }> {
  if (vapidPublicKey) {
    return { enabled: true, publicKey: vapidPublicKey };
  }
  const vapid = await fetchPushVapidPublicKey();
  return { enabled: vapid.enabled, publicKey: vapid.publicKey };
}

function pushDisabledError(): ApiRequestError {
  return new ApiRequestError({
    statusCode: 503,
    code: 'PUSH_DISABLED',
    message: 'Push ist auf dem Server derzeit nicht verfügbar.',
    timestamp: new Date().toISOString(),
    path: '/notifications/push/vapid-public-key',
  });
}

/**
 * Registers `/sw.js`, uses an existing browser Push subscription or creates one,
 * then POST /notifications/push/subscribe.
 */
export async function subscribeAndSyncPushSubscription(options?: {
  vapidPublicKey?: string;
  /** When false, only sync an existing subscription (no pushManager.subscribe). */
  createIfMissing?: boolean;
}): Promise<SubscribePushResult | null> {
  if (!isPushSupported()) {
    return null;
  }

  const createIfMissing = options?.createIfMissing ?? true;
  const vapidConfig = await resolveVapidConfig(options?.vapidPublicKey);
  if (!vapidConfig.enabled || !vapidConfig.publicKey) {
    throw pushDisabledError();
  }

  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    if (!createIfMissing) {
      return null;
    }
    if (readNotificationPermission() !== 'granted') {
      return null;
    }
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidConfig.publicKey),
    });
  }

  await postPushSubscribe(browserSubscriptionToRequest(subscription.toJSON()));
  return { endpoint: subscription.endpoint };
}

export async function readCurrentPushEndpoint(): Promise<string | null> {
  if (!isPushSupported()) {
    return null;
  }
  try {
    // Wait for the in-flight silent sync so we do not treat this browser as
    // "no subscription" while the service worker is still becoming ready.
    await registerPushOnce();
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager.getSubscription();
    return subscription?.endpoint ?? null;
  } catch {
    return null;
  }
}

/** Unsubscribes this browser's push endpoint on the server when one exists. */
export async function unregisterThisBrowserPushDevice(): Promise<void> {
  const endpoint = await readCurrentPushEndpoint();
  if (!endpoint) {
    return;
  }
  await unregisterCurrentPushDevice(endpoint);
}

/** Unsubscribes the browser push endpoint and revokes it on the server. */
export async function unregisterCurrentPushDevice(endpoint: string): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription?.endpoint === endpoint) {
      await subscription.unsubscribe();
    }
    await postPushUnsubscribeCurrent(endpoint);
  } catch {
    // Server row may already be revoked; browser cleanup is best-effort.
  }
}

/** Registers `/sw.js` once per page load and syncs an existing granted subscription. */
export async function registerPushOnce(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }
  registerGate ??= (async () => {
    try {
      if (readNotificationPermission() !== 'granted') {
        return;
      }
      await subscribeAndSyncPushSubscription({ createIfMissing: false });
    } catch {
      // Background registration must not block login; profile offers manual retry.
    }
  })();
  await registerGate;
}

/** @internal Resets the once-per-load guard (tests only). */
export function resetRegisterPushOnceForTests(): void {
  registerGate = null;
}
